import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import * as Sentry from '@sentry/react';
import { Associate, LoginPayload, RegisterAssociatePayload } from '../model';
import {
  httpClient,
  useGetAssociate,
  usePerformLogin,
  usePerformLoginFirebase,
  usePerformLogout,
  useRegisterAssociate,
} from '../hooks/hero';
import { AxiosError } from 'axios';
import { sessionActions, sessionSelectors, useAppDispatch, useAppSelector } from '../redux';

enum VerifyAuthenticatedState {
  Loading = 'loading',
  Error = 'error',
  Idle = 'idle',
  Completed = 'completed',
}
type AuthenticationState = {
  isAuthenticated: boolean;
  verifyAuthenticatedState: VerifyAuthenticatedState;
  user: Associate | undefined;
  login: (payload: LoginPayload) => Promise<void>;
  logout: (navigateTo?: string) => void;
  loginStatus: 'loading' | 'error' | 'idle' | 'success';
  logoutStatus: 'loading' | 'error' | 'idle' | 'success';
  token: string;
  registerAssociate: (payload: RegisterAssociatePayload) => Promise<boolean>;
  refetchUser: () => void;
};

const AuthenticationContext = createContext<AuthenticationState>(
  {} as AuthenticationState,
);

const isStandalone = window.top === window;

const AuthenticationProvider: FC<PropsWithChildren> = (props) => {
  const token = useAppSelector(
    sessionSelectors.selectCurrentLoginToken,
  );
  const dispatch = useAppDispatch();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [verifyAuthenticatedState, setVerifyAuthenticatedState] =
    useState<VerifyAuthenticatedState>(token || !isStandalone ? VerifyAuthenticatedState.Idle : VerifyAuthenticatedState.Completed);
  const loginMutation = usePerformLogin();
  const logoutMutation = usePerformLogout();
  const loginFirebaseMutation = usePerformLoginFirebase();
  const registerAssociateMutation = useRegisterAssociate();

  const { data: user, refetch } = useGetAssociate({
    isAuthenticated,
  });

  const logout = useCallback(
    (navigateTo = '/') => {
      logoutMutation.mutateAsync({ installationId: 'web' }).finally(() => {
        dispatch(sessionActions.setLoginToken(''));
        setIsAuthenticated(false);
        window.location.href = navigateTo;
      });
    },
    [logoutMutation, dispatch],
  );

  useEffect(() => {
    if (!token) return;

    // Verify the token
    const verifyToken = async () => {
      try {
        await httpClient.get('/users/v3/me');
        setIsAuthenticated(true);
        setVerifyAuthenticatedState(VerifyAuthenticatedState.Completed);
      } catch (e) {
        if (!(e instanceof AxiosError) || e.status !== 401) {
          // Lets treat this as success for now
          setVerifyAuthenticatedState(VerifyAuthenticatedState.Completed);
          throw e;
        }
        setVerifyAuthenticatedState(VerifyAuthenticatedState.Error);
        logout();
      }
    };

    if (verifyAuthenticatedState === VerifyAuthenticatedState.Idle) {
      setVerifyAuthenticatedState(VerifyAuthenticatedState.Loading);
      verifyToken();
    }
  }, [logout, verifyAuthenticatedState, token]);

  useEffect(() => {
    // Set Sentry context for current user and retailer
    const id = `${isAuthenticated && user?.id ? user.id : ''}`;
    const retailerId = `${isAuthenticated && user?.retailer ? user.retailer : ''}`;
    const storeId = `${isAuthenticated && user?.storeId ? user.storeId : ''}`;
    Sentry.setUser({
      id,
    });
    Sentry.setTags({
      retailerId,
      storeId,
    });
  }, [user, isAuthenticated]);

  const handleAuthenticationSuccess = useCallback((token: string) => {
    dispatch(sessionActions.setLoginToken(token));
    setVerifyAuthenticatedState(VerifyAuthenticatedState.Idle);
  }, [dispatch]);

  const [firebaseLoginContext, setFirebaseLoginContext] = useState();
  useEffect(() => {
    if (token || firebaseLoginContext || isStandalone) return;
    const messageListener = async (event: MessageEvent) => {
      if (event.data.eventName !== 'loginContext') return;
      window.removeEventListener('message', messageListener);
      setFirebaseLoginContext(event.data.payload);
      const { data } = await loginFirebaseMutation.mutateAsync(event.data.payload);
      handleAuthenticationSuccess(data.token);
    };
    window.addEventListener('message', messageListener);
    window.top?.postMessage({
      eventName: 'getLoginContext',
      microFrontend: 'chat-web-app',
    }, '*');
    return () => {
      window.removeEventListener('message', messageListener);
    };
  }, [token, firebaseLoginContext, loginFirebaseMutation, handleAuthenticationSuccess]);

  const login = async (payload: { email: string; password: string }) => {
    try {
      const { data } = await loginMutation.mutateAsync(payload);
      handleAuthenticationSuccess(data.token);
    } catch (e) {
      console.error(e);
    }
  };

  const registerAssociate = async (payload: RegisterAssociatePayload) => {
    try {
      const { data } = await registerAssociateMutation.mutateAsync(payload);
      handleAuthenticationSuccess(data.token);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const refetchUser = () => {
    refetch();
  };

  return (
    <AuthenticationContext.Provider
      value={{
        isAuthenticated,
        verifyAuthenticatedState,
        user,
        token,
        login,
        loginStatus: loginMutation.status,
        logout,
        logoutStatus: logoutMutation.status,
        registerAssociate,
        refetchUser,
      }}
    >
      {props.children}
    </AuthenticationContext.Provider>
  );
};

export { AuthenticationProvider, AuthenticationContext };
